import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { formatSerbianDate } from '../../../utilities/helper/date.helper';
import { ChildBalanceDto } from '../../../common/interfaces/invoicing.interface';
import { getBalances } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
interface ParentBillingSectionProps {
  kindergartenId: string;
  parentId: string;
  kindergartenName: string;
}

/**
 * What this parent owes, across every child they pay for.
 *
 * A parent pays one sum for two children, so the family total is the number that
 * matters when they call - and until now it existed nowhere: the debts screen lists
 * children, and nothing tied siblings back to the person actually paying.
 *
 * Only children where this parent is marked as payer appear. A parent linked to a child
 * without being its payer is not billed for it, and showing that debt here would be
 * wrong rather than merely unhelpful.
 */
const ParentBillingSection: React.FC<ParentBillingSectionProps> = ({ kindergartenId, parentId, kindergartenName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [balances, setBalances] = useState<ChildBalanceDto[]>([]);
  const [loader, setLoader] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoader(true);
    getBalances(kindergartenId, { payerId: parentId })
      .then(result => {
        if (cancelled) return;
        setBalances(result.results?.items ?? []);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoader(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, parentId]);

  if (loader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (failed) {
    return null;
  }

  const totalOutstanding = balances.reduce((sum, balance) => sum + balance.outstanding, 0);
  const totalCredit = balances.reduce((sum, balance) => sum + balance.credit, 0);

  return (
    <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 3, mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontFamily: 'Poppins' }}>{t('billing.title')}</Typography>
        {!!balances.length && (
          <Typography sx={{ fontSize: 15 }}>
            {t('billing.owedInTotal')}{' '}
            {/* the same three tones as the child's tiles: red only when something is owed */}
            <Box component="span" sx={{ fontWeight: 700, color: totalOutstanding > 0 ? palette.error.main : 'text.primary' }}>
              {formatMoney(totalOutstanding, { withCurrency: true })}
            </Box>
            {totalCredit > 0 && (
              <Box component="span" sx={{ color: palette.info.main }}> · {t('billing.inCredit', { amount: formatMoney(totalCredit, { withCurrency: true }) })}</Box>
            )}
          </Typography>
        )}
      </Stack>

      {!balances.length ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('billing.parentNotPayer')}
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('common.child')}</TableCell>
              <TableCell>{t('common.group')}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{t('finance.figures.invoiced')}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{t('finance.figures.paid')}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{t('finance.figures.outstanding')}</TableCell>
              <TableCell sx={{ width: '16%' }}>{t('common.status')}</TableCell>
              <TableCell sx={{ width: '10%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {balances.map(balance => (
              <TableRow key={balance.childId} hover>
                <TableCell>{balance.childFullName}</TableCell>
                <TableCell>{balance.groupName ?? '—'}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{formatMoney(balance.invoicedTotal)}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{formatMoney(balance.paidTotal)}</TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: balance.outstanding ? 700 : 400 }}>
                  {formatMoney(balance.outstanding)}
                </TableCell>
                <TableCell>
                  {balance.overdueCount > 0 && (
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      label={
                        balance.oldestDueDate
                          ? t('billing.overdueSince', { count: balance.overdueCount, date: formatSerbianDate(balance.oldestDueDate) })
                          : t('billing.overdue', { count: balance.overdueCount })
                      }
                    />
                  )}
                  {balance.credit > 0 && <Chip size="small" color="info" variant="outlined" label={t('billing.creditAmount', { amount: formatMoney(balance.credit) })} />}
                </TableCell>
                <TableCell>
                  <GenericButton
                    buttonType={GenericButtonTypeEnum.Secondary}
                    variant="outlined"
                    label={t('common.open')}
                    onClick={() =>
                      navigate(`/${kindergartenId}/child/${balance.childId}/details/${encodeURIComponent(kindergartenName)}`)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default ParentBillingSection;
